import { useTheme } from "next-themes"
import { Toaster as Sonner, toast } from "sonner"
import {
  CheckCircle,
  WarningCircle,
  XCircle,
  Info,
  CircleNotch,
} from "@phosphor-icons/react"

/**
 * App-wide toast surface.
 *
 * Linear/Vercel-style: a filled status icon in its own tinted well, a bold
 * title with a lighter description under it, and a left accent bar carrying
 * the status colour. `richColors` is deliberately off — the palette below is
 * ours so toasts match the studios rather than sonner's defaults.
 */
const Toaster = ({ ...props }) => {
  const { theme = "system" } = useTheme()

  // Literal class strings — Tailwind cannot see interpolated names at build time.
  const WELL = {
    success: "grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-emerald-50 text-emerald-600",
    error:   "grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-red-50 text-red-600",
    warning: "grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-amber-50 text-amber-600",
    info:    "grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-blue-50 text-[#002FA7]",
    loading: "grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-gray-100 text-gray-500",
  }

  return (
    <Sonner
      theme={theme}
      className="toaster group"
      gap={10}
      offset={16}
      visibleToasts={4}
      closeButton
      icons={{
        success: <span className={WELL.success}><CheckCircle size={17} weight="fill" /></span>,
        error:   <span className={WELL.error}><XCircle size={17} weight="fill" /></span>,
        warning: <span className={WELL.warning}><WarningCircle size={17} weight="fill" /></span>,
        info:    <span className={WELL.info}><Info size={17} weight="fill" /></span>,
        loading: <span className={WELL.loading}><CircleNotch size={17} className="animate-spin" weight="bold" /></span>,
      }}
      toastOptions={{
        duration: 4500,
        classNames: {
          toast:
            "group toast pointer-events-auto flex w-full items-start gap-3 rounded-xl border border-gray-200 bg-white p-3.5 " +
            "shadow-[0_8px_28px_-6px_rgba(16,24,40,0.16),0_2px_6px_-2px_rgba(16,24,40,0.08)] " +
            "border-l-4 border-l-gray-300 transition-all",
          success:     "border-l-emerald-500",
          error:       "border-l-red-500",
          warning:     "border-l-amber-500",
          info:        "border-l-[#002FA7]",
          loading:     "border-l-gray-300",
          icon:        "m-0 shrink-0",
          content:     "flex min-w-0 flex-col gap-0.5",
          title:       "font-heading text-[13px] font-bold leading-snug text-gray-900",
          description: "text-[12px] leading-snug text-gray-500",
          actionButton:
            "shrink-0 rounded-lg bg-[#002FA7] px-2.5 py-1.5 text-[11px] font-bold text-white transition-colors hover:bg-blue-800",
          cancelButton:
            "shrink-0 rounded-lg border border-gray-200 px-2.5 py-1.5 text-[11px] font-medium text-gray-600 transition-colors hover:bg-gray-50",
          closeButton:
            "border-gray-200 bg-white text-gray-400 transition-colors hover:text-gray-700",
        },
      }}
      {...props} />
  );
}

export { Toaster, toast }
